import React, { useState } from 'react';
import { UserNotification } from '@go-shared/types/userNotification';
import { changeStatus } from '@go-client/lib/api/userNotification';
import {
  BubbleTableFields,
  BubbleTableTemplate,
  BubbleTableProps,
  BubbleTableFieldBehavior,
} from './BubbleUI/BubbleTable';
import { BubbleBadge, BubbleButton, BubbleTable } from './BubbleUI';
import { User } from '@go-shared/types/user';
import { hashStringToColor } from '@go-shared/lib/utils';
import { notUndefined } from '@go-shared/lib/functional';
import GroupsGlance from './GroupsGlance';

export interface NotificationTableItem {
  id: string;
  date: string;
  title: string;
  category: UserNotification['category'];
  propertyName: string;
  description: string;
  _data: UserNotification;
}

interface NotificationTableViewPropsBase {
  user: User;
  tableItems: NotificationTableItem[];
  refreshNotifications: () => Promise<void>;
}

type NotificationTableViewProps = Omit<BubbleTableProps<NotificationTableItem>, 'property'> &
  NotificationTableViewPropsBase;

export default function NotificationTableView(props: NotificationTableViewProps) {
  const [selectedNotifId, setSelectedNotifId] = useState<string | undefined>(undefined);

  const { user, tableItems, refreshNotifications, ...rest } = props;

  async function markNotificationAsRead(notif: UserNotification) {
    if (notif.status === 'read') return;

    await changeStatus(notif._id, 'read');

    await refreshNotifications();
  }

  const tableFields: Array<BubbleTableFields<NotificationTableItem>> = [
    {
      key: 'date',
    },
    {
      key: 'propertyName',
      label: 'property',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'category',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'title',
      behavior: BubbleTableFieldBehavior.NoWrap,
    },
    {
      key: 'description',
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<NotificationTableItem>> = [
    {
      key: 'date',
      render: item => (
        <div className="flex items-center">
          {item._data.status === 'new' && (
            <BubbleBadge
              variant={item._data.tier > 1 ? 'crimson' : 'navy'}
              circle
              className="p-1 mr-3"
            />
          )}
          <div>
            {item.date.split(', ').map(d => (
              <div key={d} className="whitespace-nowrap">
                {d}
              </div>
            ))}
          </div>
        </div>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'propertyName',
      render: item =>
        item.propertyName ? (
          <BubbleBadge className="mt-1" color={hashStringToColor(item.propertyName)}>
            {item.propertyName}
          </BubbleBadge>
        ) : (
          <></>
        ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'category',
      render: item => (
        <BubbleBadge className="mt-1" variant="forest">
          {item.category}
        </BubbleBadge>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'description',
      render: item => <div dangerouslySetInnerHTML={{ __html: item.description }} />,
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'title',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'actions',
      render: item => (
        <>
          {item._data.category === 'groups' && (
            <BubbleButton
              className="p-2-i"
              variant="navy"
              onClick={() => setSelectedNotifId(item.id === selectedNotifId ? undefined : item.id)}
              inverse
              icon={
                item.id === selectedNotifId
                  ? 'arrow-down-left-and-arrow-up-right-to-center'
                  : 'arrow-up-right-and-arrow-down-left-from-center'
              }
            />
          )}
          {item._data.status === 'new' && (
            <BubbleButton
              className="p-2-i"
              variant="dirt"
              inverse
              onClick={() => markNotificationAsRead(item._data)}
              icon="trash-alt"
            />
          )}
        </>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
  ];

  function makeDefaultTemplateTdProps(
    data: NotificationTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const isExpandedNotif = selectedNotifId === data.id;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isExpandedNotif ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  function tableDetailsRender(item: NotificationTableItem) {
    const { property } = item._data;
    if (!property) return <></>;

    switch (item._data.category) {
      case 'groups':
        return (
          <GroupsGlance
            user={user}
            property={property}
            groupId={item._data.data.bookingId}
            bookingId={item._data.data.requestId}
          />
        );
      default:
        return <></>;
    }
  }

  return (
    <BubbleTable
      {...rest}
      striped
      fields={tableFields}
      items={tableItems}
      templates={tableTemplates}
      detailsKey={selectedNotifId}
      detailsRender={tableDetailsRender}
      detailsClassName="bg-bubblegum-lightest"
    />
  );
}
